import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { loadProposalStructures } from '../../utils/proposalStructureApi';
import {
  buildStructureMentionToken,
  filterStructuresForQuery,
} from '../../utils/proposalStructureMentions';
import {
  filterProjectOptionsForQuery,
  type StructureMentionProjectOption,
} from '../../utils/structureMentionProjectOptions';
import { applyPlainCommentListKeydown } from '../../utils/richCommentListShortcuts';

type MenuPhase = 'project' | 'structure';

interface StructureMentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  defaultDealId: string;
  defaultStructures: StructureRef[];
  projectOptions?: StructureMentionProjectOption[];
  enableProjectPicker?: boolean;
  placeholder?: string;
  rows?: number;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

function detectSlashTrigger(
  text: string,
  cursor: number,
): { start: number; query: string } | null {
  const before = text.slice(0, cursor);
  const match = before.match(/\/([^/\[\]]*)$/);
  if (!match) return null;
  return { start: cursor - match[0].length, query: match[1] };
}

const StructureMentionTextarea: React.FC<StructureMentionTextareaProps> = ({
  value,
  onChange,
  defaultDealId,
  defaultStructures,
  projectOptions = [],
  enableProjectPicker = false,
  placeholder = 'Leave a comment… (/ to tag a structure)',
  rows = 4,
  className = '',
  autoFocus,
  disabled,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuOpenRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPhase, setMenuPhase] = useState<MenuPhase>('structure');
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scopedDealId, setScopedDealId] = useState(defaultDealId);
  const [scopedStructures, setScopedStructures] = useState<StructureRef[]>(defaultStructures);
  const [scopeLabel, setScopeLabel] = useState<string | null>(null);
  const [popoverRect, setPopoverRect] = useState<DOMRect | null>(null);

  const allProjectOptions = useMemo(() => {
    if (!enableProjectPicker) return [];
    const opts = [...projectOptions];
    if (defaultDealId && !opts.some((o) => o.dealId === defaultDealId)) {
      opts.unshift({ dealId: defaultDealId, label: 'This proposal' });
    }
    return opts;
  }, [enableProjectPicker, projectOptions, defaultDealId]);

  useEffect(() => {
    setScopedDealId(defaultDealId);
    setScopedStructures(defaultStructures);
    if (defaultDealId) setScopeLabel(null);
  }, [defaultDealId, defaultStructures]);

  useEffect(() => {
    if (scopedDealId === defaultDealId) {
      setScopedStructures(defaultStructures);
      return;
    }
    let cancelled = false;
    void loadProposalStructures(scopedDealId).then((list) => {
      if (!cancelled) setScopedStructures(list);
    });
    return () => {
      cancelled = true;
    };
  }, [scopedDealId, defaultDealId, defaultStructures]);

  const projectItems = filterProjectOptionsForQuery(allProjectOptions, query);
  const structureItems = filterStructuresForQuery(scopedStructures, query);

  const activeItems = menuPhase === 'project' ? projectItems : structureItems;

  const openMenuForSlash = useCallback(() => {
    if (enableProjectPicker && allProjectOptions.length > 1) {
      setMenuPhase('project');
      return;
    }
    setMenuPhase('structure');
    if (enableProjectPicker && allProjectOptions.length === 1) {
      setScopedDealId(allProjectOptions[0].dealId);
      setScopeLabel(allProjectOptions[0].label);
    } else if (!enableProjectPicker) {
      setScopedDealId(defaultDealId);
    }
  }, [enableProjectPicker, allProjectOptions, defaultDealId]);

  const updateSuggest = useCallback(() => {
    const el = textareaRef.current;
    if (!el || disabled) {
      menuOpenRef.current = false;
      setMenuOpen(false);
      return;
    }
    const hit = detectSlashTrigger(value, el.selectionStart ?? 0);
    if (!hit) {
      menuOpenRef.current = false;
      setMenuOpen(false);
      return;
    }
    const justOpened = !menuOpenRef.current;
    menuOpenRef.current = true;
    setMenuOpen(true);
    if (justOpened) {
      openMenuForSlash();
    }
    setQuery(hit.query);
    setSelectedIndex(0);
    setPopoverRect(el.getBoundingClientRect());
  }, [value, disabled, openMenuForSlash]);

  const insertStructure = (s: StructureRef) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? value.length;
    const hit = detectSlashTrigger(value, cursor);
    if (!hit) return;
    const token = buildStructureMentionToken({
      dealId: scopedDealId,
      structureId: s.id,
      name: s.name,
    });
    const next = value.slice(0, hit.start) + token + ' ' + value.slice(cursor);
    onChange(next);
    menuOpenRef.current = false;
    setMenuOpen(false);
    window.setTimeout(() => {
      const pos = hit.start + token.length + 1;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const pickProject = (opt: StructureMentionProjectOption) => {
    setScopedDealId(opt.dealId);
    setScopeLabel(opt.label);
    setMenuPhase('structure');
    setSelectedIndex(0);
    requestAnimationFrame(updateSuggest);
  };

  const backToProjects = () => {
    setMenuPhase('project');
    setSelectedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!menuOpen) {
      applyPlainCommentListKeydown(e, e.currentTarget, onChange, 'enter');
      return;
    }
    if (e.key === 'ArrowDown') {
      if (activeItems.length === 0) return;
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % activeItems.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      if (activeItems.length === 0) return;
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + activeItems.length) % activeItems.length);
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (menuPhase === 'structure') {
        const s = structureItems[selectedIndex];
        if (!s) return;
        e.preventDefault();
        insertStructure(s);
      } else {
        const p = projectItems[selectedIndex];
        if (!p) return;
        e.preventDefault();
        pickProject(p);
      }
      return;
    }
    if (e.key === 'Escape') {
      menuOpenRef.current = false;
      setMenuOpen(false);
    }
  };

  const showProjectBack =
    enableProjectPicker && menuPhase === 'structure' && allProjectOptions.length > 1;

  return (
    <div className="por-structure-mention-field">
      <textarea
        ref={textareaRef}
        className={className}
        value={value}
        disabled={disabled}
        rows={rows}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          requestAnimationFrame(updateSuggest);
        }}
        onKeyDown={handleKeyDown}
        onClick={updateSuggest}
        onKeyUp={updateSuggest}
        onSelect={updateSuggest}
      />
      {menuOpen &&
        popoverRect &&
        createPortal(
          <div
            className="rce-mention-popover por-structure-mention-popover"
            role="listbox"
            style={{
              position: 'fixed',
              top: Math.min(popoverRect.bottom + 4, window.innerHeight - 200),
              left: popoverRect.left,
              zIndex: 10050,
            }}
          >
            {menuPhase === 'project' && (
              <div className="rce-mention-group">Choose project</div>
            )}
            {menuPhase === 'structure' && (
              <div className="rce-mention-group">
                {enableProjectPicker
                  ? `Structures${scopeLabel ? ` — ${scopeLabel}` : ''}`
                  : 'Structures'}
              </div>
            )}
            {showProjectBack && (
              <button
                type="button"
                className="rce-mention-option por-structure-mention-back"
                onMouseDown={(e) => {
                  e.preventDefault();
                  backToProjects();
                }}
              >
                ← Change project
              </button>
            )}
            {menuPhase === 'project' &&
              (projectItems.length === 0 ? (
                <div className="rce-mention-empty">No matching projects</div>
              ) : (
                projectItems.map((opt, i) => (
                  <button
                    key={opt.dealId}
                    type="button"
                    className={`rce-mention-option${i === selectedIndex ? ' active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickProject(opt);
                    }}
                  >
                    <span className="rce-mention-option-name">{opt.label}</span>
                  </button>
                ))
              ))}
            {menuPhase === 'structure' &&
              (structureItems.length === 0 ? (
                <div className="rce-mention-empty">No matching structures</div>
              ) : (
                structureItems.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`rce-mention-option${i === selectedIndex ? ' active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertStructure(s);
                    }}
                  >
                    <span className="rce-mention-option-name">{s.name}</span>
                  </button>
                ))
              ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default StructureMentionTextarea;
